import { Check, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import AppLogoIcon from '@/components/icon/app-logo-icon';

export default function ComparisonTable() {
    const { t } = useTranslation();

    const comparisonData = [
        {
            criteria: t('home.comparison.criteria.permanentlyFree'),
            heykaution: true,
            classicalBanks: false,
        },
        {
            criteria: t('home.comparison.criteria.digitalOpening'),
            heykaution: true,
            classicalBanks: false,
        },
        {
            criteria: t('home.comparison.criteria.paperlessManagement'),
            heykaution: true,
            classicalBanks: false,
        },
        {
            criteria: t('home.comparison.criteria.automaticTaxCalculation'),
            heykaution: true,
            classicalBanks: false,
        },
        {
            criteria: t('home.comparison.criteria.fastDigitalPayout'),
            heykaution: true,
            classicalBanks: false,
        },
        {
            criteria: t('home.comparison.criteria.depositInsurance'),
            heykaution: true,
            classicalBanks: true,
        },
        {
            criteria: t('home.comparison.criteria.unlimitedDeposits'),
            heykaution: true,
            classicalBanks: false,
        },
    ];

    return (
        <div className="landing-defer-paint bg-gradient-to-b from-gray-50 to-white py-12 sm:py-16 lg:py-24 dark:bg-gradient-to-b dark:from-gray-950 dark:to-gray-900" id="comparison">
            <div className="mx-auto w-full px-6 lg:px-8">
                <div className="mx-auto max-w-5xl text-center">
                    <h2 className="text-sm font-semibold tracking-wide uppercase text-hs-blue dark:text-hs-green-light">
                        {t('home.comparison.subtitle')}
                    </h2>
                    <h2 className="mt-4 text-4xl font-semibold tracking-tight text-pretty text-headline sm:text-5xl lg:text-balance">
                        {t('home.comparison.title')}
                    </h2>
                    <p className="mt-6 text-lg leading-relaxed text-paragraph">
                        {t('home.comparison.description')}
                    </p>
                </div>

                {/* Desktop Table */}
                <div className="hidden sm:block mx-auto mt-16 max-w-5xl">
                    <div className="rounded-2xl overflow-hidden dark:bg-gray-800">
                        <Table className="w-full">
                            <TableHeader>
                                <TableRow className="border-b border-gray-200 dark:border-gray-700 dark:hover:bg-gray-950">
                                    <TableHead className="text-left text-base font-semibold text-primary dark:text-white py-6 px-6 w-1/2">
                                        {/* Empty header for criteria column */}
                                    </TableHead>
                                    <TableHead className="text-center text-base font-semibold text-[var(--hs-blue)] dark:text-[var(--hs-green-light)] py-6 px-6 w-1/4">
                                        <div className="flex justify-center">
                                            <AppLogoIcon className="h-8 w-auto" />
                                        </div>
                                    </TableHead>
                                    <TableHead className="text-center text-base font-semibold text-headline py-6 px-6 w-1/4">
                                        {t('home.comparison.classicalBanks')}
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {comparisonData.map((item, index) => (
                                    <TableRow
                                        key={index}
                                        className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-950 transition-colors"
                                    >
                                        <TableCell className="text-left text-base font-semibold leading-relaxed text-headline py-4 px-6">
                                            {item.criteria}
                                        </TableCell>
                                        <TableCell className="text-center py-4 px-6">
                                            {item.heykaution ? (
                                                <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-100 dark:bg-emerald-300/30">
                                                    <Check className="w-5 h-5 text-green-600 dark:text-hs-green-light" />
                                                </div>
                                            ) : (
                                                <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-100 dark:bg-red-600/20">
                                                    <X className="w-5 h-5 text-red-600 dark:text-red-400" />
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-center py-4 px-6">
                                            {item.classicalBanks ? (
                                                <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-100 dark:bg-emerald-300/30">
                                                    <Check className="w-5 h-5 text-green-600 dark:text-hs-green-light" />
                                                </div>
                                            ) : (
                                                <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-100 dark:bg-red-600/20">
                                                    <X className="w-5 h-5 text-red-600 dark:text-red-400" />
                                                </div>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </div>

                {/* Mobile Stacked Layout */}
                <div className="sm:hidden mx-auto mt-16 max-w-lg">
                    <div className="rounded-2xl bg-white border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
                        {/* Mobile Column Headers */}
                        <div className="px-4 py-5 border-b-2 border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 rounded-t-2xl">
                            <div className="grid grid-cols-2 gap-4 items-center">
                                <div className="text-center flex justify-center">
                                    <AppLogoIcon className="h-8 w-auto" />
                                </div>
                                <div className="text-center">
                                    <div className="text-sm font-semibold text-headline">
                                        {t('home.comparison.classicalBanks')}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Mobile Criteria Rows */}
                        <div className="px-4 py-6 space-y-6">
                            {comparisonData.map((item, index) => (
                                <div key={index} className={index !== comparisonData.length - 1 ? "pb-6 border-b border-gray-100 dark:border-gray-700" : ""}>
                                    {/* Criteria Header */}
                                    <div className="mb-4">
                                        <h3 className="text-sm font-semibold leading-relaxed text-headline text-center">
                                            {item.criteria}
                                        </h3>
                                    </div>

                                    {/* Comparison Row */}
                                    <div className="grid grid-cols-2 gap-4">
                                        {/* heyKaution Column */}
                                        <div className="text-center">
                                            {item.heykaution ? (
                                                <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-100 dark:bg-emerald-300/30">
                                                    <Check className="w-5 h-5 text-green-600 dark:text-hs-green-light" />
                                                </div>
                                            ) : (
                                                <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-100 dark:bg-red-600/20">
                                                    <X className="w-5 h-5 text-red-600 dark:text-red-400" />
                                                </div>
                                            )}
                                        </div>

                                        {/* Classical Banks Column */}
                                        <div className="text-center">
                                            {item.classicalBanks ? (
                                                <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-100 dark:bg-emerald-300/30">
                                                    <Check className="w-5 h-5 text-green-600 dark:text-hs-green-light" />
                                                </div>
                                            ) : (
                                                <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-100 dark:bg-red-600/20">
                                                    <X className="w-5 h-5 text-red-600 dark:text-red-400" />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
